import { useIntl, FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'

// LuxKit Components
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import Heading from 'LuxKit/Typography/Heading'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import TextButton from 'LuxKit/Button/TextButton'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import SolidExclamationCircleIcon from 'LuxKit/Icons/solid/SolidExclamationCircleIcon'
import TextLink from 'LuxKit/TextLink'
import type { CalendarItemBundleData } from '../hooks/types'

// Utils
import { getInventoryMessage, getCTAMessage } from '../hooks/utils'
import { buildSearchParamsForFilterPane } from 'components/Pages/DestinationLandingPage/DestinationLandingPageUtils'
import Places from 'constants/places'
import config from 'constants/config'
import { encodeChildrenOccupancy } from 'lib/search/searchUtils'
import { mergeOccupants } from 'lib/offer/occupancyUtils'
import KidsFlyFreeLabel from 'components/Common/Labels/KidsFlyFreeLabel'
import useKidsFlyFree from 'hooks/CharterFlights/useKidsFlyFree'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import type { FlightsClassTypes } from 'constants/flight'

// Types
interface TripOption {
  id: string
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  price: number
  memberPrice?: number
  currency: string
  seatsLeft?: number
  nights: number
  available: boolean
}

interface Props {
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  nights: number
  priceMode: 'total' | 'perPerson'
  luxPlusEnabled: boolean
  isSelected: boolean
  bundleData: CalendarItemBundleData
  onSelect: (option: TripOption) => void
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
  /** When provided (offer page), Kids Fly Free label is shown only if offer has campaign tag and date qualifies */
  offer?: { campaignTags?: Array<string> }
}

const SelectButton = styled(TextButton)`
  width: ${rem(90)};
`

const InventoryLabelGroup = styled(Group)`
  min-width: ${rem(155)};
`

const SmallSup = styled.sup`
  font-size: 0.6em;
  vertical-align: super;
`

function MaldivesOfferCalendarItem(props: Props) {
  const {
    departDate,
    returnDate,
    checkIn,
    checkOut,
    nights,
    priceMode,
    luxPlusEnabled,
    isSelected,
    onSelect,
    bundleData,
    rooms,
    fareClass,
    offer,
  } = props

  const intl = useIntl()
  const { isKidsFlyFreeDate, shouldShowKidsFlyFreeForOffer } = useKidsFlyFree({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    occupancies: rooms,
    fareClass,
  })

  // Merge multiple rooms into single occupancy for search params
  const mergedRoom = rooms.length > 0 ? mergeOccupants(rooms) : { adults: 2, children: 0, childrenAge: [] }
  const adultsCount = mergedRoom.adults
  const childrenParam = encodeChildrenOccupancy(mergedRoom.childrenAge)

  // Select price based on priceMode
  const regularPrice = priceMode === 'perPerson' ? bundleData.pricePerPerson : bundleData.totalPrice
  const memberPrice = priceMode === 'perPerson' ? bundleData.memberPricePerPerson : bundleData.memberTotalPrice

  const isMemberPrice = luxPlusEnabled && !!memberPrice
  const displayPrice = isMemberPrice && memberPrice ? memberPrice : regularPrice

  const option: TripOption = {
    id: `${checkIn}-${nights}n`,
    departDate: departDate,
    returnDate: returnDate,
    checkIn: checkIn,
    checkOut: checkOut,
    nights,
    price: regularPrice,
    memberPrice: memberPrice,
    currency: 'AUD',
    seatsLeft: bundleData.seatsLeft,
    available: Boolean(bundleData.available),
  }

  const selectedText = intl.formatMessage({
    id: 'acuxSK1zMav7-Sn9mh4K',
    defaultMessage: 'Selected',
    description: 'Button label when a charter flight date is currently selected',
  })
  const selectText = intl.formatMessage({
    id: 'MvGwxY9AMe6ETPfczwZK',
    defaultMessage: 'Select',
    description: 'Button label to select a charter flight date in the flights flow',
  })
  const unavailableText = intl.formatMessage({
    id: 'MkdIPUtrVEQzhLPF4i4b',
    defaultMessage: 'Unavailable',
    description: 'Button label when charter flight date is unavailable',
  })
  const noPackageText = intl.formatMessage({
    id: 'J7ASvqQn0CMBMT9iseru',
    defaultMessage: 'No package available',
    description: 'Text shown when no hotel package is available for charter flight date',
  })
  const loadingText = intl.formatMessage({
    id: 'C6m8UbIIgveZbgrQtJ6T',
    defaultMessage: 'Loading...',
    description: 'Loading state text while charter flight prices are being fetched',
  })
  const nightsAndFlightsText = intl.formatMessage(
    {
      id: 'r484bkJ_LnqWqW8S-s9D',
      defaultMessage: '{nights, plural, one {# night} other {# nights}} & flights',
      description: 'Label showing number of nights and flights included in charter bundle',
    },
    { nights },
  )
  const dayOffsetText = intl.formatMessage({
    id: 'l9QfkSZq5TknjYVpdoEi',
    defaultMessage: '+1',
    description: 'Day offset indicator showing arrival is next day for charter flight',
  })

  // Render select button based on state
  const selectButton = (isSelected && bundleData.available) ? (
    <SelectButton
      kind="primary"
      nonInteractable
      tabIndex={-1}
    >
      {selectedText}
    </SelectButton>
  ) : (
    <SelectButton
      kind="secondary"
      onClick={() => onSelect(option)}
      disabled={!bundleData.available || bundleData.hasNoPackage}
    >
      {bundleData.available ? selectText : unavailableText}
    </SelectButton>
  )

  // Render price display based on availability
  let priceDisplay
  if (bundleData.hasNoPackage) {
    priceDisplay = (
      <BodyText variant="small" colour="neutral-three">
        {noPackageText}
      </BodyText>
    )
  } else if (bundleData.fetching) {
    priceDisplay = (
      <BodyText variant="small" colour="neutral-three">
        {loadingText}
      </BodyText>
    )
  } else if (displayPrice > 0) {
    const perPersonLabel = intl.formatMessage({
      id: 'ew2ARy7ro3tFSM4inpHN',
      defaultMessage: '/person',
      description: 'Per person pricing unit suffix in Maldives calendar',
    })
    const totalLabel = intl.formatMessage({
      id: 'j--PuUYLTgWUxAxROh0Z',
      defaultMessage: 'total',
      description: 'Total pricing unit suffix in the Maldives calendar',
    })
    priceDisplay = (
      <>
        <PriceRowPriceCaption>{intl.formatMessage({
          id: 'Ml8XRp8vLCsgfe6Wuy0g',
          defaultMessage: 'From',
          description: 'Price prefix label shown before the amount in the Maldives offer calendar item',
        })}
        </PriceRowPriceCaption>
        <div>
          <FormattedMessage
            id="-x5_rqeYCNl-p7X7dlrF"
            defaultMessage="{price} {unit}"
            description="Price amount followed by pricing unit (per person or total) in Maldives offer calendar"
            values={{
              price: <Heading as="span" variant="heading4" colour={isMemberPrice ? 'lux-plus' : undefined} wrap="no-wrap">
                <FormatCurrency value={displayPrice} format="roundedDollar" />
              </Heading>,
              unit: <Caption as="span" variant="large" colour={isMemberPrice ? 'lux-plus' : 'neutral-two'} weight="normal" format="lowercase">
                {priceMode === 'perPerson' ? perPersonLabel : totalLabel}
              </Caption>,
            }}
          />
        </div>
      </>
    )
  } else {
    priceDisplay = (
      <BodyText variant="medium" colour="neutral-three">
        {unavailableText}
      </BodyText>
    )
  }

  return (
    <>
      {/* Column 1: Departure date */}
      <div>
        <BodyText variant="small" colour="neutral-three">
          {moment(departDate).format('dddd')}
        </BodyText>
        <Heading variant="heading4" wrap="no-wrap">
          {moment(departDate).format('DD MMM YYYY')}
        </Heading>
      </div>

      {/* Column 2: Nights */}
      <Group direction="vertical" gap={4} horizontalAlign="center">
        <LineArrowRightIcon size="M" />
        <BodyText variant="small" colour="neutral-three">
          {nightsAndFlightsText}
        </BodyText>
      </Group>

      {/* Column 3: Return date */}
      <div>
        <BodyText variant="small" colour="neutral-three">
          {moment(checkOut).format('dddd')}
        </BodyText>
        <Heading variant="heading4" wrap="no-wrap">
          {moment(checkOut).format('DD MMM YYYY')}<SmallSup>{dayOffsetText}</SmallSup>
        </Heading>
      </div>

      {/* Column 4: Kids Fly Free + Availability Warning */}
      {(() => {
        const inventoryMessage = getInventoryMessage(intl, bundleData.seatsLeft, bundleData.roomsLeft)

        return (
          <Group direction="vertical" horizontalAlign="center">
            <InventoryLabelGroup direction="vertical" horizontalAlign="start" verticalAlign="center" gap={2}>
              {inventoryMessage !== '' && <Group direction="horizontal" horizontalAlign="start" verticalAlign="center" gap={4}>
                <SolidExclamationCircleIcon colour="urgency" size="XS" />
                <BodyText variant="small" colour="critical">
                  {inventoryMessage}
                </BodyText>
              </Group>}
              {(offer ? shouldShowKidsFlyFreeForOffer(offer, checkIn) : isKidsFlyFreeDate(departDate)) && <KidsFlyFreeLabel variant="short" />}
            </InventoryLabelGroup>
          </Group>
        )
      })()}

      {/* Column 5: Price and Select Button OR CTA */}
      {(() => {
        const ctaMessage = getCTAMessage(bundleData.seatsLeft, bundleData.roomsLeft)

        // Hotel sold out - show CTA with link
        if (ctaMessage === 'hotel-sold-out') {
          return (
            <BodyText variant="medium" colour="neutral-two">
              <FormattedMessage
                id="67iYmaeI_4LikZXraHif"
                defaultMessage="<link>View similar bundles</link> for these dates"
                description="CTA when hotel is sold out, links to search for similar Maldives bundles"
                values={{
                  link: (chunks) => (
                    <TextLink
                      weight="regular"
                      to={`/search?${buildSearchParamsForFilterPane({
                        originalSearch: '',
                        destinationName: Places.Maldives.name,
                        destinationId: Places.Maldives.id,
                        adults: adultsCount,
                        children: childrenParam,
                      })}&checkIn=${checkIn}&checkOut=${checkOut}`}
                    >
                      {chunks}
                    </TextLink>
                  ),
                }}
              />
            </BodyText>
          )
        }

        // Bundle sold out - show CTA with phone
        if (ctaMessage === 'bundle-sold-out') {
          return (
            <BodyText variant="medium" colour="neutral-two">
              <FormattedMessage
                id="xrX71nYk8QpV6O_5s-kR"
                defaultMessage="Call our team on <phoneLink>{phoneNumber}</phoneLink> for more options"
                description="CTA when charter bundle is sold out, provides phone number for support"
                values={{
                  phoneLink: (chunks) => (
                    <TextLink weight="regular" to={`tel:${config.MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER}`}>
                      {chunks}
                    </TextLink>
                  ),
                  phoneNumber: config.MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER,
                }}
              />
            </BodyText>
          )
        }

        // Default - show price and select button
        return (
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={12}>
            <VerticalSpacer gap={4}>
              {priceDisplay}
            </VerticalSpacer>
            {selectButton}
          </Group>
        )
      })()}
    </>
  )
}

export default MaldivesOfferCalendarItem
